import { createApp, type App } from "vue";
import { AppType, parseAppType } from "@allwyn/build/vue/app-type";
import { parseDataProps } from "./parse-data-props";
import { resolveVueComponent } from "./resolve-vue-component";
import { setupSharedApp } from "./setup-shared-app";

const mountedRoots = new WeakSet<HTMLElement>();

/**
 * Scans the DOM for `[data-vue]` elements whose `data-app-type` is **not**
 * `AppType.Standalone` and mounts them as shared Vue apps.
 *
 * Each mounted app receives shared plugins registered in {@link setupSharedApp}.
 * This enables reactive state sharing between all shared Vue apps on the page.
 *
 * Already-mounted elements are skipped.
 */
export function mountSharedVueRoots(): void {
    mountAllRoots(setupSharedApp, false);
}

/**
 * Scans the DOM for `[data-vue]` elements whose `data-app-type` is
 * `AppType.Standalone` and mounts them as standalone Vue apps.
 *
 * Standalone apps start with a clean Vue instance — no shared plugins are
 * installed. Use the optional `setup` callback to install app-specific plugins.
 *
 * @param setup - Optional callback to configure each Vue app before it is mounted
 *
 * @example
 * ```ts
 * import { createPinia } from "pinia";
 *
 * mountStandaloneVueRoots((app) => {
 *     app.use(createPinia());
 * });
 * ```
 */
export function mountStandaloneVueRoots(setup?: (app: App) => void): void {
    mountAllRoots(setup, true);
}

function mountAllRoots(setup: ((app: App) => void) | undefined, isStandalone: boolean): void {
    const roots = document.querySelectorAll<HTMLElement>("[data-vue]");

    for (const root of roots) {
        const appType = parseAppType(root.dataset.appType);
        const rootIsStandalone = appType === AppType.Standalone;

        if (rootIsStandalone !== isStandalone) {
            continue;
        }

        mountRoot(root, appType, setup);
    }
}

async function mountRoot(root: HTMLElement, appType: AppType, setup?: (app: App) => void): Promise<void> {
    if (mountedRoots.has(root)) {
        return;
    }

    const componentName = root.dataset.vue;

    if (!componentName) {
        return;
    }

    const component = await resolveVueComponent(componentName, appType);
    const app = createApp(component.default, parseDataProps(root));

    setup?.(app);
    app.mount(root);
    mountedRoots.add(root);
}
